/**
 * ThemeExposureChart — every theme the book is exposed to, as horizontal bars.
 *
 * "Exposure" counts a name in *every* gate-satisfying theme it holds, so the rows
 * overlap and the percentages do **not** sum to 100%: two themes held by the same
 * position each carry its full value. The card ends with that caveat, boxed so it
 * still reads as a note on the numbers above rather than as the end of the list.
 *
 * Positions with no theme are drawn as a final, muted bar instead of a footnote:
 * they are the complement of the ranked rows and cannot overlap anything, so that
 * one row's share of the book is exact.
 *
 * Each row shows the absolute value *and* its share, so neither has to be inferred
 * from the bar. The bar itself is clamped, since a share can exceed 100%: the
 * percentage label can legitimately read 250% while the bar sits at full width, and
 * the label is the one telling the truth.
 *
 * A row is two claims, and each opens its own surface:
 *
 * - tapping the **count** (which carries a `+`) lists the positions behind it. The
 *   list comes from the row that was tapped, so it cannot disagree with the number
 *   that was tapped — "show me" that produced a different list would be worse than
 *   no affordance at all.
 * - tapping the **name** opens the theme's own investment case: the operator's
 *   thesis and its break conditions, fetched by slug (see `ThemeModals`).
 *
 * The row is pressable only where there is a theme behind it, so the unthemed bar
 * keeps its `+` but has no thesis to open.
 *
 * The endpoint's own `basis_exposure` sentence is shown verbatim, except when the
 * payload names its basis with a code the client can phrase — then the sentence is
 * the fallback, not the display (see `utils/themeBasis`). Either way the sentence
 * the endpoint sent is what a reader sees if the client cannot do better.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  getThemeBreakdown,
  type ThemeBreakdown,
  type ThemeBreakdownConstituent,
} from '../../services/portfolioApi';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeRegistry, registryToMap } from '../../services/api/queries/useThemeTags';
import { translateTheme } from '../../utils/translationHelpers';
import { basisText } from '../../utils/themeBasis';
import { formatAmount } from '../../utils/formatAmount';
import { ThemeHoldingsModal, ThemeThesisModal } from './ThemeModals';

/** What the positions modal is currently showing. */
interface OpenHoldings {
  name: string;
  count: number;
  holdings: ThemeBreakdownConstituent[];
}

interface OpenThesis {
  slug: string;
  name: string;
}

export function ThemeExposureChart() {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const registryQuery = useThemeRegistry();
  const registryMap = useMemo(() => registryToMap(registryQuery.data), [registryQuery.data]);

  const breakdownQuery = useQuery<ThemeBreakdown>({
    queryKey: ['theme-breakdown'],
    queryFn: getThemeBreakdown,
    staleTime: 60_000,
  });

  const data = breakdownQuery.data ?? null;
  // The endpoint returns exposure sorted by value descending, so this is already a
  // ranking and needs no client-side sort.
  const rows = data?.exposure ?? [];

  const [openHoldings, setOpenHoldings] = useState<OpenHoldings | null>(null);
  const [openThesis, setOpenThesis] = useState<OpenThesis | null>(null);

  const shareOfBook = (value: number): number =>
    data && data.total_value > 0 ? (value / data.total_value) * 100 : 0;

  const name = (slug: string): string => translateTheme(slug, registryMap, i18n.language);
  const unknownName = t('dashboard.themeExposureUnknown', { defaultValue: 'Unknown (no theme)' });

  const showHoldings = (
    themeName: string,
    count: number,
    holdings?: ThemeBreakdownConstituent[]
  ) => {
    setOpenHoldings({ name: themeName, count, holdings: holdings ?? [] });
  };

  // Everything the unthemed bar needs, resolved once: the share is used for the
  // label *and* for the bar, and the two must not be computed from different values.
  const unclassified =
    data && data.unclassified_positions > 0
      ? {
          share: shareOfBook(data.unclassified_value),
          value: data.unclassified_value,
          count: data.unclassified_positions,
          holdings: data.unclassified_holdings ?? [],
        }
      : null;

  if (breakdownQuery.isLoading) {
    return (
      <View style={[styles.card, styles.centered]}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  if (breakdownQuery.isError) {
    return (
      <View style={[styles.card, styles.centered]}>
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          {t('dashboard.themeExposureFailed', {
            defaultValue: 'Could not load theme exposure',
          })}
        </Text>
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
          onPress={() => {
            void breakdownQuery.refetch();
          }}
        >
          <Text style={[styles.retryText, { color: theme.colors.textInverse }]}>
            {t('common.retry')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.card} testID="theme-exposure-chart">
      <Text style={[styles.title, { color: theme.colors.text }]}>
        {t('dashboard.themeExposure', { defaultValue: 'Theme Exposure' })}
      </Text>

      {data && !data.tags_available ? (
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          {t('dashboard.themeRegistryUnavailable', {
            defaultValue:
              'Theme registry unavailable — this does not mean the book is in no themes.',
          })}
        </Text>
      ) : rows.length === 0 ? (
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          {t('dashboard.themeExposureEmpty', { defaultValue: 'Not mapped to any theme yet' })}
        </Text>
      ) : (
        <>
          {rows.map((row) => {
            const share = shareOfBook(row.value);
            // Clamped for the bar only. The label keeps the real number, so a share
            // over 100% shows as a figure rather than as a bar escaping its
            // container.
            const barWidth = Math.min(Math.max(share, 0), 100);
            const themeName = name(row.theme_slug);
            return (
              <TouchableOpacity
                key={row.theme_slug}
                testID={`theme-exposure-row-${row.theme_slug}`}
                style={styles.row}
                onPress={() => setOpenThesis({ slug: row.theme_slug, name: themeName })}
                accessibilityRole="button"
                accessibilityLabel={t('dashboard.themeExposureOpenThesis', {
                  defaultValue: 'Open the investment thesis for {{name}}',
                  name: themeName,
                })}
              >
                <View style={styles.rowHeader}>
                  <Text style={[styles.rowName, { color: theme.colors.text }]} numberOfLines={1}>
                    {themeName}
                  </Text>
                  <Text
                    style={[styles.rowPercentage, { color: theme.colors.primary }]}
                    testID={`theme-exposure-percent-${row.theme_slug}`}
                  >
                    {share.toFixed(1)}%
                  </Text>
                </View>
                <View style={[styles.track, { backgroundColor: theme.colors.border }]}>
                  <View
                    testID={`theme-exposure-bar-${row.theme_slug}`}
                    style={[
                      styles.bar,
                      { width: `${barWidth}%`, backgroundColor: theme.colors.primary },
                    ]}
                  />
                </View>
                <View style={styles.rowDetails}>
                  <Text
                    style={[styles.rowValue, { color: theme.colors.textSecondary }]}
                    testID={`theme-exposure-value-${row.theme_slug}`}
                  >
                    {formatAmount(row.value, data?.currency)}
                  </Text>
                  <ExpandButton
                    testID={`theme-exposure-expand-${row.theme_slug}`}
                    label={t('dashboard.themeExposureShowPositions', {
                      defaultValue: 'Show the positions counted in {{name}}',
                      name: themeName,
                    })}
                    count={row.position_count}
                    onPress={() => showHoldings(themeName, row.position_count, row.constituents)}
                    styles={styles}
                    theme={theme}
                  />
                </View>
              </TouchableOpacity>
            );
          })}

          {/* Positions with no theme get a bar of their own rather than a footnote.
              They are the complement of everything above — a different kind of row,
              not a small theme — so it is drawn in a muted fill and sits outside the
              ranking, where it cannot be mistaken for a theme that merely scored
              low. It cannot overlap anything, which is why it is the one row whose
              value genuinely is a share of the book. */}
          {unclassified && (
            <View style={styles.row} testID="theme-exposure-row-unclassified">
              <View style={styles.rowHeader}>
                <Text style={[styles.rowName, { color: theme.colors.textSecondary }]}>
                  {unknownName}
                </Text>
                <Text
                  style={[styles.rowPercentage, { color: theme.colors.textSecondary }]}
                  testID="theme-exposure-percent-unclassified"
                >
                  {unclassified.share.toFixed(1)}%
                </Text>
              </View>
              <View style={[styles.track, { backgroundColor: theme.colors.border }]}>
                <View
                  testID="theme-exposure-bar-unclassified"
                  style={[
                    styles.bar,
                    {
                      width: `${Math.min(Math.max(unclassified.share, 0), 100)}%`,
                      backgroundColor: theme.colors.textTertiary,
                    },
                  ]}
                />
              </View>
              <View style={styles.rowDetails}>
                <Text
                  style={[styles.rowValue, { color: theme.colors.textSecondary }]}
                  testID="theme-exposure-value-unclassified"
                >
                  {formatAmount(unclassified.value, data?.currency)}
                </Text>
                <ExpandButton
                  testID="theme-exposure-expand-unclassified"
                  label={t('dashboard.themeExposureShowPositions', {
                    defaultValue: 'Show the positions counted in {{name}}',
                    name: unknownName,
                  })}
                  count={unclassified.count}
                  onPress={() =>
                    showHoldings(unknownName, unclassified.count, unclassified.holdings)
                  }
                  styles={styles}
                  theme={theme}
                />
              </View>
            </View>
          )}

          {/* Verbatim from the server unless the code tells us how to phrase it. */}
          {data?.basis_exposure ? (
            <Text
              style={[styles.caption, { color: theme.colors.textTertiary }]}
              testID="theme-exposure-basis"
            >
              {basisText(t, data.basis_exposure_code, data.basis_exposure)}
            </Text>
          ) : null}

          {/* Last, as asked. Boxed so it still reads as a caveat on the numbers above
              rather than as the end of the list. */}
          <View
            style={[
              styles.remark,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
            testID="theme-exposure-remark"
          >
            <Text style={[styles.remarkText, { color: theme.colors.textSecondary }]}>
              {t('dashboard.themeExposureOverlap', {
                defaultValue:
                  'A position can hold several themes, so these percentages do not add up to 100%.',
              })}
            </Text>
          </View>
        </>
      )}

      <ThemeHoldingsModal
        visible={!!openHoldings}
        name={openHoldings?.name ?? ''}
        count={openHoldings?.count ?? 0}
        holdings={openHoldings?.holdings ?? []}
        currency={data?.currency ?? null}
        onClose={() => setOpenHoldings(null)}
      />

      <ThemeThesisModal
        visible={!!openThesis}
        slug={openThesis?.slug ?? null}
        name={openThesis?.name ?? ''}
        onClose={() => setOpenThesis(null)}
      />
    </View>
  );
}

interface ExpandButtonProps {
  testID: string;
  label: string;
  count: number;
  onPress: () => void;
  styles: ReturnType<typeof createThemedStyles>;
  theme: any;
}

/**
 * The count, as the thing you press to see the names behind it.
 *
 * The label and the glyph share one touch target: a bare `+` at this size is a
 * small target, and the count is what a reader is actually reaching for when they
 * want to know *which* positions. It sits inside a row that is itself pressable, so
 * it carries its own testID and accessibility label to stay distinguishable.
 */
function ExpandButton({ testID, label, count, onPress, styles, theme }: ExpandButtonProps) {
  const { t } = useTranslation();
  return (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      style={styles.expandButton}
    >
      <Text style={[styles.rowCount, { color: theme.colors.textTertiary }]}>
        {t('dashboard.themeExposurePositions', {
          defaultValue: '{{count}} position(s)',
          count,
        })}
      </Text>
      <Text style={[styles.expandGlyph, { color: theme.colors.primary }]}>+</Text>
    </TouchableOpacity>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: 16,
      marginBottom: 16,
    },
    centered: {
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 120,
    },
    title: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 12,
    },
    remark: {
      borderWidth: 1,
      borderRadius: theme.radii.md,
      padding: 10,
      marginBottom: 12,
    },
    remarkText: {
      fontSize: theme.typography.fontSizeXs,
      lineHeight: theme.typography.fontSizeXs * 1.5,
    },
    row: {
      marginBottom: 12,
    },
    rowHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
    },
    rowName: {
      flex: 1,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginRight: 8,
    },
    rowPercentage: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    track: {
      height: 8,
      borderRadius: theme.radii.sm,
      overflow: 'hidden',
    },
    bar: {
      height: '100%',
      borderRadius: theme.radii.sm,
    },
    rowDetails: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 2,
    },
    rowValue: {
      fontSize: theme.typography.fontSizeXs,
    },
    rowCount: {
      fontSize: theme.typography.fontSizeXs,
    },
    expandButton: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    expandGlyph: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      marginLeft: 6,
      lineHeight: theme.typography.fontSizeMd,
    },
    caption: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
      paddingVertical: 12,
    },
    retryButton: {
      marginTop: 8,
      paddingHorizontal: 20,
      paddingVertical: 8,
      borderRadius: theme.radii.md,
    },
    retryText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
